import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { customerClassificationSchema } from '@/schemas/customer-classification-schema'
import type {
	CustomerClassificationInput,
	CustomerClassificationOutput,
} from '@/types/customer-classification'

export interface CustomerClassificationFormProps {
	mode: 'create' | 'edit'
	initial?: CustomerClassificationOutput
	onSave: (data: CustomerClassificationInput) => void
	onClose: () => void
}

export function CustomerClassificationFormContent({
	mode,
	initial,
	onSave,
	onClose,
}: CustomerClassificationFormProps) {
	const { t } = useTranslation()
	const [name, setName] = useState(initial?.name ?? '')

	const handleSave = () => {
		const parsed = customerClassificationSchema.safeParse({ name })
		if (!parsed.success) {
			toast.error(
				t('administration.customerClassifications.form.invalidNameError'),
			)
			return
		}
		onSave(parsed.data)
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="customer-classification-name">
					{t('administration.customerClassifications.form.nameLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="customer-classification-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t(
							'administration.customerClassifications.form.namePlaceholder',
						)}
						maxLength={255}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.customerClassifications.form.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create'
						? t('administration.customerClassifications.form.saveButton')
						: t('administration.customerClassifications.form.saveEditButton')}
				</Button>
			</div>
		</div>
	)
}
